import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  Paper,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import CampaignIcon from '@mui/icons-material/Campaign';
import CategoryIcon from '@mui/icons-material/Category';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ArticleIcon from '@mui/icons-material/Article';
import ProjectDonationFlow from '../components/ProjectDonationFlow';
import ActivityWebFlow from '../components/ActivityWebFlow';
import ProjectActivityHowItWorks from '../components/ProjectActivityHowItWorks';

export default function ProjectActivityManagement() {
  const navigate = useNavigate();

  return (
    <>
      {/* ================= HERO ================= */}
      <Box
        sx={{
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.06)} 0%, #ffffff 100%)`,
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#3b82f6', 0.12)} 0%, transparent 70%)`,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box maxWidth="720px">
            {/* Category Chip */}
            <Chip
              label="Proje & Faaliyet Yönetimi Modülü"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: '0.8rem',
                background: alpha('#3b82f6', 0.12),
                color: '#2563eb',
                border: `1px solid ${alpha('#3b82f6', 0.2)}`,
              }}
            />

            {/* Title */}
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.1,
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Projelerinizi ve Faaliyetlerinizi{' '}
              <Box component="span" sx={{
                background: `linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Tek Merkezden Yönetin
              </Box>
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#64748b',
                lineHeight: 1.7,
                mb: 5,
              }}
            >
              Projelerinizi bağışlarla ilişkilendirin, faaliyetlerinizi detaylandırın.
              Her iki içeriği de web sitenizde ve widget'larda otomatik olarak yayınlayın.
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                //onClick={() => navigate('/iletisim')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: '1rem',
                  borderColor: alpha('#3b82f6', 0.3),
                  '&:hover': {
                    borderColor: '#3b82f6',
                    background: alpha('#3b82f6', 0.05),
                  },
                }}
              >
                Hemen Başlayın
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
                Proje Yönetimi
              </Typography>

              <Typography sx={{
                fontSize: '1.05rem',
                color: '#475569',
                lineHeight: 1.8,
                mb: 4,
                maxWidth: 520,
              }}>
                Projeler, derneğinizin hedef odaklı çalışmalarını temsil eder.
                Bütçe, bağış ve süreç takibi ile birlikte yönetilir.
              </Typography>

              <Stack spacing={2}>
                <FeatureItem
                  icon={<CategoryIcon />}
                  text="Proje kategorileri oluşturun"
                />
                <FeatureItem
                  icon={<LinkIcon />}
                  text="Projeleri bağış ve kampanyalarla ilişkilendirin"
                />
                <FeatureItem
                  icon={<CampaignIcon />}
                  text="Bütçe, toplanan tutar ve bağışçı sayısını izleyin"
                />
                <FeatureItem
                  icon={<PublicIcon />}
                  text="Projeleri web sitenizde yayınlayın veya gizleyin"
                />
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  height: { xs: 300, md: 470 },
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: '1px solid #e5e7eb',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                  mx: 'auto',
                }}
              >
                <Box
                  component={'img'}
                  src='/assets/page_images/projectPage.webp'
                  alt="Proje Yönetimi"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center" flexDirection={{ xs: 'column-reverse', md: 'row' }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  maxWidth: 700,
                  height: { xs: 300, md: 470 },
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: '1px solid #e5e7eb',
                  background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                  mx: 'auto',
                }}
              >
                <Box
                  component={'img'}
                  src='/assets/page_images/faaliyetPage.webp'
                  alt="Faaliyet Yönetimi"
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h3" fontWeight={700} mb={2} color="#0f172a">
                Faaliyet Yönetimi
              </Typography>

              <Typography sx={{
                fontSize: '1.05rem',
                color: '#475569',
                lineHeight: 1.8,
                mb: 4,
                maxWidth: 520,
              }}>
                Faaliyetler, derneğinizin yaptığı işleri ve sahadaki çalışmaları
                temsil eder. Her faaliyet aynı zamanda bir içeriktir.
              </Typography>

              <Stack spacing={2}>
                <FeatureItem
                  icon={<CategoryIcon />}
                  text="Faaliyet kategorileri oluşturun"
                />
                <FeatureItem
                  icon={<ArticleIcon />}
                  text="Detaylı açıklama ve içerik ekleyin"
                />
                <FeatureItem
                  icon={<WidgetsIcon />}
                  text="Widget’lar ile web sitesinde yayınlayın"
                />
                <FeatureItem
                  icon={<PublicIcon />}
                  text="Faaliyetleri aktif/pasif olarak yönetin"
                />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ProjectActivityHowItWorks />

      <ProjectDonationFlow />

      <ActivityWebFlow />

      <Box
        sx={{
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              background: `linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)`,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 4,
              py: { xs: 6, md: 8 },
              px: { xs: 3, md: 6 },
            }}
          >
            {/* Arka plan deseni */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.15,
                backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
                backgroundSize: '30px 30px'
              }}
            />

            {/* Decorative circles */}
            <Box
              sx={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                filter: 'blur(60px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '-10%',
                left: '-5%',
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(50px)',
              }}
            />

            <Box sx={{ position: 'relative', textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  fontWeight: 900,
                  mb: 3,
                  textShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                Projelerinizi ve Faaliyetlerinizi Dijitalleştirin
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  mb: 5,
                  opacity: 0.95,
                  maxWidth: '620px',
                  mx: 'auto',
                  lineHeight: 1.7,
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                Proje ve faaliyet yönetimini tek platformda birleştirin. 
                İçeriklerinizi otomatik olarak web sitenizde ve widget'larda yayınlayın.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/basvuru')}
                  sx={{
                    bgcolor: 'white',
                    color: '#3b82f6',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: '#fafafa',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  Hemen Başvurun
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/iletisim')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 5,
                    py: 2,
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    borderWidth: 2,
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255,255,255,0.1)',
                    '&:hover': {
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  İletişime Geçin
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

/* ================= Küçük Bileşen ================= */

function FeatureItem({ icon, text }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ color: '#2563eb', display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontWeight: 600, color: '#334155' }}>{text}</Typography>
    </Stack>
  );
}